import React, { useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';

import { InputField } from 'shared/forms';
import { useTranslation } from 'hooks/useTranslation';
import useValidation from 'hooks/useValidation';
import BaseButton from 'features/app/components/BaseButton';
import { SelectInputField } from 'features/app/components/SelectInputField';
import { OutputBrandedGC } from 'features/app/ts/OutputBrandedGC';
import { QuantityInputField } from 'features/app/components/QuantityInputField';
import { usePartnerBalanceQuery } from 'features/app/hooks/usePartnerBalanceQuery';
import { BalanceCurrency } from 'features/app/ts/BalanceCurrency';
import { BrandedGCCurrency } from 'features/my-gift-cards/ts/BrandedGCCurrency';
import SelectInputScrollLoadingField from 'features/app/components/SelectInputScrollLoadingField';
import { getBrandedGiftCards } from 'features/app/api';

const getCurrenciesFromProduct = (product: OutputBrandedGC, balanceCurrencies: BalanceCurrency[]) => {
  const resultCurrencies = [];

  for (const productCurrency of product?.currencies) {
    if (balanceCurrencies) {
      const { id, currency_name, currency_code, ...rest } = productCurrency;
      resultCurrencies.push({
        ...rest,
        id,
        currency_code,
        currency_name,
        value: id,
        label: currency_name,
      });
    }
  }

  return resultCurrencies;
};

interface GiftInformationStepProps {
  onSubmit: () => void;
}

export const GiftInformationStep: React.FC<GiftInformationStepProps> = ({ onSubmit }) => {
  const { t } = useTranslation('dashboard');
  const { required, maxNumber, minNumber } = useValidation();
  const { data: balanceCurrencies } = usePartnerBalanceQuery();

  const { trigger, getValues, setValue, setError } = useFormContext();

  const product: OutputBrandedGC = getValues('product');
  const currency: BrandedGCCurrency = getValues('currency');

  const productCurrencies = useMemo(() => {
    if (!product || !balanceCurrencies) return;

    return getCurrenciesFromProduct(product, balanceCurrencies);
  }, [product, balanceCurrencies]);

  const minCardAmount = useMemo(() => {
    if (!currency) return;

    return currency.min_denomination_value;
  }, [currency]);

  const maxCardAmount = useMemo(() => {
    if (!currency) return;

    return currency.max_denomination_value;
  }, [currency]);

  const handleProductChange = useCallback(() => {
    setError('currency', { message: '' });
    setError('amount', { message: '' });
    setValue('currency', null);
    setValue('amount', 1);
  }, [setError, setValue]);

  const handleSubmit = async () => {
    const isFormValid = await trigger(['product', 'currency', 'amount', 'quantity']);
    isFormValid && onSubmit();
  };

  return (
    <form>
      <Box display="flex" justifyContent="space-between" paddingBottom={20.5}>
        <Box paddingRight={15} flexBasis={'50%'}>
          <SelectInputScrollLoadingField
            name="product"
            label={t('product')}
            onChange={handleProductChange}
            fetchItems={getBrandedGiftCards}
            transformRequestResponse={response =>
              response.map(({ id, product_name, ...values }) => ({
                id,
                value: id as string | number,
                label: product_name,
                ...values,
              }))
            }
            placeholder={t('selectFromList')}
            rules={{
              validate: {
                ...required,
              },
            }}
          />
        </Box>

        <Box paddingLeft={15} flexBasis={'50%'}>
          <SelectInputField
            name="currency"
            label={t('currencies')}
            placeholder={t('selectFromList')}
            isDisabled={!productCurrencies}
            options={productCurrencies}
            rules={{
              validate: {
                ...required,
              },
            }}
          />
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" paddingBottom={20.5}>
        <InputField
          name="amount"
          type="number"
          disabled={!minCardAmount && !maxCardAmount}
          label={t('giftCardAmount')}
          rules={{
            validate: {
              ...required,
              ...maxNumber(maxCardAmount || 1),
              ...minNumber(minCardAmount || 1),
            },
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column" paddingBottom={20.5}>
        <QuantityInputField
          min={1}
          max={15}
          label={t('quantity')}
          placeholder={t('quantity')}
          rules={{
            validate: {
              ...required,
              ...maxNumber(15),
              ...minNumber(1),
            },
          }}
        />
      </Box>
      <Grid container alignItems="center" justifyContent="center">
        <BaseButton onClick={handleSubmit} maxWidth={269}>
          {t('next')}
        </BaseButton>
      </Grid>
    </form>
  );
};
